// The top bar (docs/DESIGN.md, "The shell"): the product switcher, an "Admin tools on"
// chip when the server says so, and the account menu.
//
// NO VENDOR MARK. No brand or wordmark at the left: the work in the spotlight
// is the teaching, not whoever built the software. The district goes there instead — whose
// tool this is. School Improvement browses many districts, so it names none.
//
// NO IDENTITY IS SHOWN. The API returns no name or role, by design, and the browser's own sign-in
// isn't read for one either — this app doesn't store or show who you are (docs/IDENTITY.md).

import { useState } from "react";
import type { District } from "../activities";
import { PRODUCTS, type Product } from "../products";
import { applyAppearance, savedAppearance, type Appearance } from "./appearance";
import { Icon } from "./Icon";
import { useSignOut } from "./signOut";
import { useMenu } from "./useMenu";

interface Props {
  product: Product;
  products: Product[];
  isAdmin: boolean;
  district: District | null;
  navOpen: boolean;
  onToggleNav: () => void;
  onProduct: (p: Product) => void;
  /** School years this teacher has a plan for, newest first. Only shown when there is a choice. */
  years?: number[];
  year?: number | null;
  onYear?: (y: number) => void;
  // The admin reference. In this menu rather than the sidebar because Student Learning's sidebar IS
  // its curriculum plan (`Shell`), so there is no tab strip to add a row to.
  onGlossary?: () => void;
}

export function TopBar({ product, products, isAdmin, district, navOpen, onToggleNav, onProduct,
                        years, year, onYear, onGlossary }: Props) {
  return (
    <header className="topbar">
      <button className="chrome-btn icon menu-toggle" aria-label={navOpen ? "Close navigation" : "Open navigation"}
              aria-expanded={navOpen} aria-controls="sidebar" onClick={onToggleNav}>
        <Icon name="menu" size={20} />
      </button>
      <ProductMenu product={product} products={products} onProduct={onProduct} />
      {district && <span className="district" title={district.name}>{district.name}</span>}
      <span className="spacer" />
      {isAdmin && <span className="admin-hint">Admin tools on</span>}
      <AccountMenu isAdmin={isAdmin} years={years} year={year} onYear={onYear}
                   onGlossary={onGlossary} />
    </header>
  );
}

function ProductMenu({ product, products, onProduct }:
    { product: Product; products: Product[]; onProduct: (p: Product) => void }) {
  const m = useMenu("product");
  // One product on offer: a name, not a switcher. A menu of one is a control that does nothing.
  if (products.length < 2) {
    return <span className="chrome-btn"><span className="prod-dot" aria-hidden="true" />{PRODUCTS[product].label}</span>;
  }
  return (
    <div className="menu-wrap">
      <button className="chrome-btn boxed" aria-haspopup="menu" {...m.buttonProps}>
        <span className="prod-dot" aria-hidden="true" />{PRODUCTS[product].label}<Icon name="chevD" size={14} />
      </button>
      <div className="menu prod-menu" role="menu" aria-label="Switch product" {...m.menuProps}>
        <div className="menu-head">Products</div>
        {products.map((p) => (
          <button key={p} role="menuitem" className="menu-item" aria-current={p === product}
                  onClick={() => { m.close(); if (p !== product) onProduct(p); }}>
            <span className="sw" data-product={p} aria-hidden="true" />
            <span className="menu-item-body"><b>{PRODUCTS[p].label}</b><small>{PRODUCTS[p].blurb}</small></span>
            {p === product && <Icon name="check" />}
          </button>
        ))}
      </div>
    </div>
  );
}

/** A school year, Ed-Fi's ending year: 2027 is 2026-27, and that is how it is shown. */
function yearLabel(y: number): string {
  return `${y - 1}–${String(y).slice(2)}`;
}

function AccountMenu({ isAdmin, years, year, onYear, onGlossary }: {
  isAdmin: boolean;
  years?: number[];
  year?: number | null;
  onYear?: (y: number) => void;
  onGlossary?: () => void;
}) {
  const m = useMenu("account");
  const signOut = useSignOut();
  const [appearance, setAppearance] = useState<Appearance>(savedAppearance);
  const choose = (a: Appearance) => { applyAppearance(a); setAppearance(a); };
  return (
    <div className="menu-wrap">
      <button className="chrome-btn icon" aria-label="Account and appearance" aria-haspopup="menu" {...m.buttonProps}>
        <span className="acct"><Icon name="user" /></span>
      </button>
      <div className="menu right" role="menu" aria-label="Account" {...m.menuProps}>
        <div className="menu-note">
          You're signed in. This app doesn't store or show who you are.
          {isAdmin && <><br />Admin tools are shown for this account.</>}
        </div>
        {/* Tucked in here on purpose: looking at a past year is rare, and a control in the
            chrome for something done twice a term costs more attention than it returns. Absent
            entirely when there is only one year — a menu of one is a control that does nothing,
            the same rule the product switcher follows. */}
        {years && years.length > 1 && onYear && (
          <>
            <div className="menu-head">School year</div>
            {years.map((y) => (
              <button key={y} role="menuitemradio" aria-checked={y === year} className="menu-item"
                      onClick={() => { onYear(y); m.close(); }}>
                <span className="menu-check">{y === year && <Icon name="check" />}</span>
                {yearLabel(y)}
              </button>
            ))}
            <div className="menu-rule" />
          </>
        )}
        <div className="menu-head">Appearance</div>
        {(["system", "light", "dark"] as const).map((a) => (
          <button key={a} role="menuitemradio" aria-checked={appearance === a} className="menu-item"
                  onClick={() => choose(a)}>
            <span className="menu-check">{appearance === a && <Icon name="check" />}</span>
            {a === "system" ? "System" : a === "light" ? "Light" : "Dark"}
          </button>
        ))}
        {isAdmin && onGlossary && (
          <>
            <div className="menu-rule" />
            <button role="menuitem" className="menu-item"
                    onClick={() => { m.close(); onGlossary(); }}>Glossary</button>
          </>
        )}
        {signOut && (
          <>
            <div className="menu-rule" />
            <button role="menuitem" className="menu-item" onClick={() => { m.close(); signOut(); }}>Sign out</button>
          </>
        )}
      </div>
    </div>
  );
}
